import { Injectable } from '@angular/core';
import { BaseModelKeys } from 'ag-common-lib/lib/models/base.model';
import {
  EmailTemplateConfiguration,
  EmailTemplateConfigurationKeys,
  EmailTemplateModel,
  EmailTemplatesModels,
  Conference,
  Registrant,
  RegistrantModelKeys,
} from 'ag-common-lib/public-api';
import {
  EventInquiryRequest,
  EventInquiryRequestKeys,
} from 'ag-common-lib/lib/models/registration/event-inquiry-request.model';
import { BaseFormService } from 'ag-common-svc/public-api';
import { RegistrationParticipationEmailService } from 'ag-common-svc/lib/services/registration-participation-email.service';
import { AttendeesEmailsService } from 'src/app/ag-crm/registrations/attendees-emails.service';
import { CloudFunctionsService } from 'ag-common-svc/lib/services/cloud-functions.service';
export type ConferenceTestEmailFormData = {
  to: string[];
  emailTemplate: EmailTemplateModel;
  emailTemplateConfiguration: EmailTemplateConfiguration;
  payload?: Registrant | EventInquiryRequest;
};

@Injectable()
export class ConferenceEmailTestEmailService extends BaseFormService {
  private readonly dynamicTemplatePrefix = 'dynamic:';

  constructor(
    private cloudFunctionsService: CloudFunctionsService,
    private registrationParticipationEmailService: RegistrationParticipationEmailService,
    private attendeesEmailsService: AttendeesEmailsService,
  ) {
    super();
  }

  async sendToEmail(testEmailFormData: ConferenceTestEmailFormData, conference?: Partial<Conference>): Promise<boolean> {
    this.startProgress();
    try {
      if (!testEmailFormData) {
        console.warn('Test email data is missing.');
        return false;
      }

      const emailRecipientTestMode = testEmailFormData.to;
      const emailTemplate = testEmailFormData.emailTemplate;
      const emailTemplateConfiguration = testEmailFormData.emailTemplateConfiguration;
      const templateName = emailTemplateConfiguration?.[EmailTemplateConfigurationKeys.templateName];

      if (this.isDynamicTemplateName(templateName)) {
        const templateDbId = this.getDynamicTemplateId(templateName);
        const registrant: Registrant = testEmailFormData.payload;
        const registrantDbId = registrant?.[BaseModelKeys.dbId];
        const conferenceDbId =
          registrant?.[RegistrantModelKeys.conferenceDbId] ?? conference?.[BaseModelKeys.dbId];
        const subject = emailTemplateConfiguration?.[EmailTemplateConfigurationKeys.subject];
        const requiredFields = emailTemplateConfiguration?.[EmailTemplateConfigurationKeys.payload]?.requiredFields;

        if (!templateDbId || !registrantDbId || !conferenceDbId) {
          console.warn('Dynamic test email requires template, registrant, and conference IDs.');
          return false;
        }

        await this.cloudFunctionsService.sendEmailTemplate({
          templateDbId,
          subject,
          to: emailRecipientTestMode,
          registrantDbId,
          conferenceDbId,
          isTestEmail: true,
          sender: emailTemplateConfiguration?.[EmailTemplateConfigurationKeys.sender],
          attachments: emailTemplateConfiguration?.[EmailTemplateConfigurationKeys.attachments],
          requiredFields,
        });

        return true;
      }

      switch (emailTemplate?.model) {
        case EmailTemplatesModels.registrationInquiry: {
          const eventInquiryRequest = testEmailFormData.payload as EventInquiryRequest;
          const emailActionTrigger = emailTemplateConfiguration?.[EmailTemplateConfigurationKeys.trigger];
          const eventInquiryRequestDbId = eventInquiryRequest?.[BaseModelKeys.dbId];
          const eventInquiryDbId = eventInquiryRequest?.[EventInquiryRequestKeys.eventInquiryDbId];

          await this.registrationParticipationEmailService.sendRegistrationParticipationEmail({
            isTestEmail: true,
            eventInquiryDbId,
            emailRecipientTestMode,
            emailActionTrigger,
            eventInquiryRequestDbId,
          });
          break;
        }

        case EmailTemplatesModels.registrant: {
          const registrant = testEmailFormData.payload as Registrant;

          await this.attendeesEmailsService.sendEmails(emailTemplateConfiguration, {
            registrants: [registrant],
            isTestEmail: true,
            emailRecipientTestMode,
          });
          break;
        }

        default:
          console.warn('Unknown email template model for test email.', emailTemplate?.model);
          break;
      }

      return true;
    } catch (error) {
      console.error('Failed to send test email.', error);
      return false;
    } finally {
      this.stopProgress();
    }
  }

  private isDynamicTemplateName(value: unknown): value is string {
    return typeof value === 'string' && value.startsWith(this.dynamicTemplatePrefix);
  }

  private getDynamicTemplateId(value: string | null | undefined): string | null {
    if (!value || !this.isDynamicTemplateName(value)) {
      return null;
    }
    const id = value.slice(this.dynamicTemplatePrefix.length).trim();
    return id || null;
  }
}
